import { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  MenuItem,
  LinearProgress,
  Box,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { FilterGrid, FilterSelect, FilterMenuItem, AssignmentMultiSelect } from "components/common/FilterControls";
import useDialog from "hooks/useDialog";
import useAccess from "hooks/useAccess";
import axiosInstance from "utilities/axios";

const Report = () => {
  const { showSnackbar } = useDialog();
  const { isMonitoring, isUniversal, isCgs } = useAccess();
  const [assignments, setAssignments] = useState([]);

  // Build component options from active assignment types
  const components = [
    ...(!isMonitoring || isUniversal ? [{ title: "უნივერსალური", value: "monitoring" }] : []),
    ...(!isMonitoring || isCgs ? [{ title: "მიკრო (CGS)", value: "monitoring_cgs" }] : []),
  ];

  // Agency/admin only: fetch assignments for the filter dropdown
  useEffect(() => {
    if (!isMonitoring) {
      axiosInstance
        .get(`${process.env.REACT_APP_BASE_URL}assignments`)
        .then((resp) => setAssignments(resp.data || []))
        .catch(() => { });
    }
  }, [isMonitoring]);

  const [filterData, setFilterData] = useState({
    component: "all",
    status: "all",
    monitoring_assignment: [],
  });

  const [reportLoading, setReportLoading] = useState(false);

  const downloadReport = (endpoint, fileName) => {
    setReportLoading(true);
    axiosInstance({
      method: "post",
      url: `${process.env.REACT_APP_BASE_URL}${endpoint}`,
      data: filterData,
      responseType: "blob",
    })
      .then((response) => {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setReportLoading(false);
      })
      .catch((error) => {
        const reader = new FileReader();
        reader.readAsText(error.response.data);
        reader.onload = () => {
          try {
            const res = JSON.parse(reader.result);
            showSnackbar(res.message || "დაფიქსირდა შეცდომა", "error");
          } catch {
            showSnackbar("დაფიქსირდა შეცდომა", "error");
          }
        };
        setReportLoading(false);
      });
  };

  const generateReport = () => downloadReport("generate_report", "ApplicationsReport.xlsx");

  const filteredAssignments = assignments.filter((a) => {
    if (filterData.component === "monitoring") return a.application_type === "universal";
    if (filterData.component === "monitoring_cgs") return a.application_type === "cgs";
    return true;
  });

  return (
    <Card>
      <CardHeader title="რეპორტის გენერირება" sx={{ borderBottom: "1px solid #f3f3f3" }} />
      <CardContent>
        <FilterGrid>
          <FilterSelect
            label="კომპონენტი"
            labelId="component-label"
            value={filterData.component}
            onChange={(e) => setFilterData({ ...filterData, component: e.target.value, monitoring_assignment: [] })}
          >
            <FilterMenuItem value="all"><em>აირჩიეთ</em></FilterMenuItem>
            {components.map((comp) => (
              <FilterMenuItem key={comp.value} value={comp.value}>{comp.title}</FilterMenuItem>
            ))}
          </FilterSelect>

          {!isMonitoring && filterData.component !== "all" && filteredAssignments.length > 0 && (
            <AssignmentMultiSelect
              label="მონიტორინგი"
              value={filterData.monitoring_assignment}
              onChange={(val) => setFilterData({ ...filterData, monitoring_assignment: val })}
              assignments={filteredAssignments}
            />
          )}
        </FilterGrid>

        <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
          <Button
            disabled={reportLoading || filterData.component === "all"}
            color="primary"
            variant="contained"
            startIcon={<CloudDownloadIcon />}
            onClick={generateReport}
          >
            ჩამოტვირთვა
          </Button>
        </Box>
      </CardContent>
      <LinearProgress sx={{ display: reportLoading ? "block" : "none" }} />
    </Card>
  );
};

export default Report;
